"use client";
import { useEffect, useMemo, useState } from "react";
import DesktopNav from "@/components/header/desktop-nav";
import HeaderActions from "@/components/header/header-actions";
import MobileMenu from "@/components/header/mobile-menu";
import Logo from "@/components/logo";
import { useBodyScrollLock } from "@/hooks/use-header";
import { useOptimisticBasket } from "@/hooks/use-optimistic-basket";
import { useScrollDirection } from "@/hooks/use-scroll-direction";
import { useShopFilters } from "@/hooks/use-shop-filters";
import { useSSRSafeViewport } from "@/hooks/use-ssr-safe-viewport";
import {
  getCategoryDescription,
  type ProductCategory,
  routes,
  useCategoryDisplayNames,
} from "@/utils/routes";

// Static fallback categories
const FALLBACK_CATEGORIES: ProductCategory[] = [
  "protein",
  "pre-workout",
  "vitamins",
  "creatine",
  "fat-burners",
  "recovery",
];

const SUPPORT_LINKS = [
  { title: "About Us", href: routes.about },
  { title: "FAQ", href: routes.faq },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Fetch dynamic categories from Shopify
  const { availableCategories } = useShopFilters();
  const getCategoryDisplayName = useCategoryDisplayNames();

  // React 19 useOptimistic for basket updates
  const { basketCount, isPending } = useOptimisticBasket();

  // Custom scroll direction hook with progressive behavior
  const { isScrolled, isHeaderVisible } = useScrollDirection(
    {
      baseThreshold: 200,
      scrolledThreshold: 100,
      maxHideDelay: 800,
      hoverRevealHeight: 100,
    },
    {
      isMenuOpen,
      isSearchOpen,
    }
  );

  // SSR-safe viewport detection
  const { isMobile: isMobileView, isHydrated } = useSSRSafeViewport();

  // Body scroll lock when mobile menu is open
  useBodyScrollLock(isMenuOpen, isHydrated);

  // Inline fixed height for mobile only (no shrink animation)
  const headerInlineHeight = isHydrated && isMobileView ? "80px" : null;

  // Header transform based on scroll direction and visibility
  const headerTransform = useMemo(() => {
    if (!isHydrated) {
      return "translateY(0)";
    }

    // Never hide header if mobile menu or search is open
    if (isMenuOpen || isSearchOpen) {
      return "translateY(0)";
    }

    // Hide header when scrolling down and not visible
    return isHeaderVisible ? "translateY(0)" : "translateY(-100%)";
  }, [isHydrated, isHeaderVisible, isMenuOpen, isSearchOpen]);

  // Create memoized categories from dynamic Shopify data with fallback
  const memoizedCategories = useMemo(() => {
    // Use dynamic categories if available, otherwise fallback to static ones
    const categories =
      availableCategories?.length > 0
        ? availableCategories
        : FALLBACK_CATEGORIES;

    return categories.map((category) => ({
      category,
      url: `/shop?categories=${category}`,
      displayName: getCategoryDisplayName(category),
      description: getCategoryDescription(category),
    }));
  }, [availableCategories, getCategoryDisplayName]);
  useEffect(() => {
    const el = document.querySelector("header") as HTMLElement | null;
    if (!el) return;

    const setVar = (height: number) => {
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${Math.round(height)}px`
      );
    };

    setVar(el.offsetHeight || 80);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const h = entry.contentRect.height;
          setVar(h || 80);
        }
      });
      ro.observe(el);
    }

    const onResize = () => setVar(el.offsetHeight || 80);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ease-in-out ${isScrolled ? "border-border/40 bg-background/90 shadow-sm backdrop-blur-lg" : "border-border bg-background"}`}
      style={{ transform: headerTransform }}
      suppressHydrationWarning
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div
          className="flex h-20 items-center justify-between lg:justify-normal"
          style={
            headerInlineHeight ? { height: headerInlineHeight } : undefined
          }
        >
          <div className="hidden lg:flex lg:flex-1 lg:items-center">
            <DesktopNav
              memoizedCategories={memoizedCategories}
              SUPPORT_LINKS={SUPPORT_LINKS}
            />
          </div>

          <div className="lg:shrink-0">
            <Logo isMounted={isHydrated} isScrolled={isScrolled} />
          </div>

          <div className="flex flex-1 items-center justify-end">
            <HeaderActions
              basketCount={basketCount}
              isMenuOpen={isMenuOpen}
              isPending={isPending}
              isSearchOpen={isSearchOpen}
              setIsMenuOpen={setIsMenuOpen}
              setIsSearchOpen={setIsSearchOpen}
            />
          </div>
        </div>

        <MobileMenu
          isMenuOpen={isMenuOpen}
          memoizedCategories={memoizedCategories}
          SUPPORT_LINKS={SUPPORT_LINKS}
          setIsMenuOpen={setIsMenuOpen}
        />
      </div>
    </header>
  );
}
